import { useEffect, useRef } from 'react';

const DEFAULT_MIN_FONT_SIZE = 4;

export function useWidgetAdaptiveText({
  minFontSize = DEFAULT_MIN_FONT_SIZE,
  padding = 0,
}: {
  minFontSize?: number;
  padding?: number;
}) {
  const textRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = textRef.current;
    const parent = el?.parentElement;
    if (!el || !parent) {
      return;
    }

    const fit = () => {
      el.style.fontSize = '';
      let fontSize = parseFloat(getComputedStyle(el).fontSize);
      const availableWidth = parent.clientWidth - padding * 2;

      while (el.scrollWidth > availableWidth && fontSize > minFontSize) {
        fontSize -= 0.5;
        el.style.fontSize = `${fontSize}px`;
      }
    };

    fit();

    const resizeObserver = new ResizeObserver(() => fit());
    resizeObserver.observe(parent);

    // The parent keeps its size when only the text changes — a language switch, or a countdown
    // crossing into a longer format — so the resize observer alone would leave the old font size on
    // a string that no longer fits. Watching the text itself refits on those. Only childList and
    // characterData are observed: `fit` writes to style, an attribute, so it cannot loop.
    const textObserver = new MutationObserver(() => fit());
    textObserver.observe(el, { characterData: true, childList: true, subtree: true });

    return () => {
      resizeObserver.disconnect();
      textObserver.disconnect();
    };
  }, [minFontSize, padding]);

  return { textRef };
}
